/**
 * The ISP's brand colour as the console's primary colour (buttons, links, active tabs, focus rings, the active menu
 * item, hover tints) and every blue / violet accent of the console — light and dark theme. Without a brand colour the
 * Netxol blue → violet stays.
 * A light brand colour gets dark text on it so buttons stay readable.
 */
const STYLE_ID = 'nx-brand-theme';

function hsl(hex: string) {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
  const max = Math.max(r, g, b); const min = Math.min(r, g, b); const l = (max + min) / 2;
  let h = 0; let s = 0;
  if (max !== min) {
    const d = max - min; s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4; h *= 60;
  }
  return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100), lum: 0.2126 * r + 0.7152 * g + 0.0722 * b };
}
const darker = (hex: string, k = 0.75) => '#' + [1, 3, 5].map((i) => Math.round(parseInt(hex.slice(i, i + 2), 16) * k).toString(16).padStart(2, '0')).join('');

/** Lightness of each shade against the 600 shade (Tailwind's blue and violet), so a brand scale keeps the same contrast. */
const B_STEPS: Record<string, number> = { 50: 44, 100: 40, 200: 34, 300: 25, 400: 15, 500: 7, 600: 0, 700: -5, 800: -13, 900: -20, 950: -32 };
const A_STEPS: Record<string, number> = { 50: 40, 100: 37, 200: 34, 300: 27, 400: 18, 500: 8, 600: 0, 700: -8, 800: -16, 900: -23, 950: -35 };
/** The blue (b) or violet (a) scale of the console in the brand's hue: the brand colour is the 600 shade. */
function shades(k: string, c: { h: number; s: number; l: number }, steps: Record<string, number>) {
  return Object.entries(steps).map(([n, d]) => `--${k}-${n}:${c.h} ${Math.min(c.s, d > 25 ? 95 : 100)}% ${Math.max(6, Math.min(98, c.l + d))}%;`).join('');
}

export function applyBrandTheme(hex?: string | null) {
  document.getElementById(STYLE_ID)?.remove();
  if (!hex || !/^#[0-9a-f]{6}$/i.test(hex)) return;
  const c = hsl(hex);
  const fg = c.lum > 0.6 ? '222 47% 11%' : '0 0% 100%';
  const dl = Math.min(Math.max(c.l, 45) + 8, 68);   // a little lighter on the dark theme
  const el = document.createElement('style');
  el.id = STYLE_ID;
  const to = hsl(darker(hex));
  el.textContent = `:root{${shades('b', c, B_STEPS)}${shades('i', c, B_STEPS)}${shades('a', to, A_STEPS)}${shades('p', to, A_STEPS)}--primary:${c.h} ${c.s}% ${c.l}%;--ring:${c.h} ${c.s}% ${c.l}%;--primary-foreground:${fg};--primary-soft:${c.h} ${Math.min(c.s, 90)}% 95%;`
    + `--nx-brand-from:${hex};--nx-brand-to:${darker(hex)};--nx-brand-shadow:${hex}40}`
    + `.dark{--primary:${c.h} ${c.s}% ${dl}%;--ring:${c.h} ${c.s}% ${dl}%;--primary-foreground:222 24% 7%;--primary-soft:${c.h} ${Math.min(c.s, 55)}% 18%}`;
  document.head.appendChild(el);
}

/* ───────── colours drawn in code (charts, maps, KPI accents) ───────── */
/** Netxol's own accent colours as written in chart and map code → the shade of the scale that replaces them. */
const NETXOL_HEX: Record<string, string> = {
  '#eff6ff': 'b-50', '#dbeafe': 'b-100', '#bfdbfe': 'b-200', '#93c5fd': 'b-300', '#60a5fa': 'b-400', '#3b82f6': 'b-500', '#2563eb': 'b-600', '#1d4ed8': 'b-700',
  '#6366f1': 'i-500', '#4f46e5': 'i-600', '#a78bfa': 'a-400', '#8b5cf6': 'a-500', '#7c3aed': 'a-600', '#6d28d9': 'a-700', '#a855f7': 'p-500', '#9333ea': 'p-600',
};
function hslToHex(ch: string) {
  const m = ch.match(/([\d.]+)\s+([\d.]+)%\s+([\d.]+)%/); if (!m) return null;
  const [h, s, l] = [Number(m[1]), Number(m[2]) / 100, Number(m[3]) / 100];
  const k = (n: number) => (n + h / 30) % 12; const a = s * Math.min(l, 1 - l);
  const f = (n: number) => Math.round(255 * (l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1))))).toString(16).padStart(2, '0');
  return `#${f(0)}${f(8)}${f(4)}`;
}
/**
 * A colour written in code, in the ISP's brand: Netxol's blue / violet accents become the brand's shades (as hex, so
 * charts can still add transparency); every other colour (status, fibre colour codes…) is returned unchanged. Without
 * a brand colour nothing changes.
 */
export function brandize<T extends string | undefined>(color: T): T {
  if (!color || typeof document === 'undefined' || !document.getElementById(STYLE_ID)) return color;
  const v = NETXOL_HEX[color.toLowerCase()]; if (!v) return color;
  return (hslToHex(getComputedStyle(document.documentElement).getPropertyValue(`--${v}`)) ?? color) as T;
}